import { ConfigProvider, theme as antdTheme } from 'antd'
import type { ReactNode } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { RequireAuth } from '../guards'
import { useEffectiveTheme } from '../ui/themePreference'
import AIChatPage from './AIChatPage'
import AudioAssetsPage from './AudioAssetsPage'
import CompetitiveContentAnalysisPage from './CompetitiveContentAnalysisPage'
import ContentProductionListPage from './contentProduction/ContentProductionListPage'
import ProductionCanvasPage from './contentProduction/canvas/ProductionCanvasPage'
import ContentProductionProjectRedirect from './contentProduction/ContentProductionProjectRedirect'
import ContentPublishingDetailPage from './ContentPublishingDetailPage'
import ContentPublishingListPage from './ContentPublishingListPage'
import WorkspaceDomainsPage from './DomainsPage'
import ImageAssetCenterPage from './ImageAssetCenterPage'
import WorkspaceImagesPage from './ImagesPage'
import TextAssetsPage from './TextAssetsPage'
import RequireTeamManager from './team/RequireTeamManager'
import TeamManagementPage from './team/TeamManagementPage'
import LocalPublisherAgentPage from './LocalPublisherAgentPage'
import FlatImageListPage from './flatImage/FlatImageListPage'
import FlatImageEditorPage from './flatImage/FlatImageEditorPage'
import RequireFeature from './RequireFeature'
import WorkspaceIndexRedirect from './WorkspaceIndexRedirect'
import CanvasAccountResumePage from './CanvasAccountResumePage'
import WorkspaceHomeEntryPage from './WorkspaceHomeEntryPage'
import {
  AccountSettingsComingSoon,
  HelpComingSoon,
  PlanUsageComingSoon,
} from './AccountComingSoonPages'
import WorkspaceLayout from './WorkspaceLayout'
import WorkspaceOverviewPage from './OverviewPage'
import { overviewPersonalProfilePath } from './overview/overviewTabKeys'
import StorageDetailPage from './StorageDetailPage'
import WorkspacePublishPage from './PublishPage'
import VideoAssetsPage from './VideoAssetsPage'
import VideoContinuationPage from './VideoContinuationPage'
import VideoGenerationPage from './VideoGenerationPage'
import VideoDirectorCreatePage from './videoDirector/VideoDirectorCreatePage'
import VideoProjectListPage from './videoDirector/VideoProjectListPage'
import VirtualIpApp from './virtualIp/VirtualIpApp'
import VirtualIpLegacyPositioningRedirect from './virtualIp/VirtualIpLegacyPositioningRedirect'
import WebsiteHomePage from './website/WebsiteHomePage'
import WebsiteSitePage from './website/WebsiteSitePage'

/** VistoeraTASK-00592 — bookmarks to `/account/profile` never render the
 * deleted standalone page; they land on Overview settings → profile. */
function LegacyAccountProfileRedirect() {
  const { workspaceId = '' } = useParams()
  return <Navigate to={overviewPersonalProfilePath(workspaceId)} replace />
}

/** GLOBAL_TABLE_UI_AND_ITEM_THUMBNAIL_P0 — the outer `App.tsx`
 * `ConfigProvider` (spec §2/§3's real dark-mode root cause) only ever set
 * `colorPrimary`/`fontFamily`, never `algorithm`/color tokens, so every
 * un-customized AntD control (Select/Input/Table filters, Dropdown menus,
 * Tag, disabled buttons, pagination…) kept rendering with AntD's *light*
 * default text/background colors regardless of `[data-theme="dark"]` —
 * exactly the "深色背景 + 深色文字" bug: only the handful of elements this
 * repo explicitly re-skins in CSS (see `tenant-items.css`) ever got
 * legible in dark mode.
 *
 * Scoped to the Workspace shell only (nested `ConfigProvider`s override
 * their subtree in AntD) — Platform Admin is a deliberately LIGHT ONLY
 * surface for now (`platform/theme.ts`) with no theme toggle of its own;
 * flipping its default-algorithm AntD controls dark just because a
 * browser previously chose dark mode in the Workspace shell would clash
 * with its still-hardcoded-light custom chrome. Public site pages are
 * unaffected for the same reason. */
function WorkspaceThemeProvider({ children }: { children: ReactNode }) {
  const effective = useEffectiveTheme()
  const dark = effective === 'dark'
  return (
    <ConfigProvider
      theme={{
        algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#5B5FF5',
          borderRadius: 6,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif',
          ...(dark
            ? {
                colorBgContainer: '#111827',
                colorBgElevated: '#172033',
                colorBgLayout: '#0b1120',
                colorBorder: '#243044',
                colorBorderSecondary: '#1e2a3c',
                colorText: '#f8fafc',
                colorTextSecondary: '#cbd5e1',
                colorTextTertiary: '#94a3b8',
                colorTextQuaternary: '#7c8aa0',
                colorTextPlaceholder: '#7c8aa0',
                colorTextDisabled: '#5b6b84',
              }
            : {}),
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}

/** Mounted at `/workspace/*` — spec §25/§31: requires an active session
 * (backend still enforces per-workspace membership on every request via
 * `require_workspace_member`/`require_site_member` — see
 * `WorkspaceLayout`'s 403 handling for the client-side reflection of
 * that). */
export default function WorkspaceApp() {
  return (
    <RequireAuth>
      <WorkspaceThemeProvider>
        <Routes>
          <Route index element={<WorkspaceIndexRedirect />} />
          <Route path=":workspaceId" element={<WorkspaceLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            {/* VISTOERA_AI_CHAT_TENANT_P0 §2 — top-of-sidebar entry. */}
            {/* VISTOERA_SAAS_TENANT_FEATURE_ENTITLEMENT_MATRIX_V1 §24 — every
             * route below that maps to a gateable Product Capability is
             * wrapped in `RequireFeature`; `overview` (Shell/base page every
             * tenant needs) and the Image Asset Center library (existing
             * assets stay viewable even when the *creation* feature is off,
             * same convention as the backend's `feature.image_processing`
             * gate) are deliberately left ungated. */}
            <Route
              path="ai-chat"
              element={
                <RequireFeature featureKey="feature.ai_chat">
                  <AIChatPage />
                </RequireFeature>
              }
            />
            <Route path="overview" element={<WorkspaceOverviewPage />} />
            {/* VISTOERA_CANVAS_AS_PRIMARY_HOME_AND_GLOBAL_ACCOUNT_ENTRY_V1
             * §4/§6 — Canvas-as-Primary-Home. Declared before
             * `content-production/:projectId/canvas` (irrelevant here,
             * literal segment) but grouped near it since it resolves onto
             * that exact same route. Ungated — same precedent as
             * `overview` (every tenant needs it, it is the new default
             * landing surface). */}
            <Route path="home" element={<WorkspaceHomeEntryPage />} />
            {/* VistoeraTASK-00581 — account-switch Canvas resume (last-opened → Home). */}
            <Route path="canvas-resume" element={<CanvasAccountResumePage />} />
            {/* VISTOERA_CANVAS_AS_PRIMARY_HOME_AND_GLOBAL_ACCOUNT_ENTRY_V1
             * §30/§34/§35/§39 — Account Popover secondary routes with no
             * real page behind them yet this round (route contract +
             * coming-soon placeholder, never a fake page / 404 — see
             * `AccountComingSoonPages.tsx`). */}
            {/* VistoeraTASK-00592 — legacy bookmark → Overview 个人设置/个人资料 */}
            <Route path="account/profile" element={<LegacyAccountProfileRedirect />} />
            <Route path="account/plan-usage" element={<PlanUsageComingSoon />} />
            <Route path="account/settings" element={<AccountSettingsComingSoon />} />
            <Route path="account/help" element={<HelpComingSoon />} />
            {/* VISTOERA_VIRTUAL_IP_PROJECT_AND_ASSET_INFORMATION_ARCHITECTURE_P0_V1 —
             * parent "虚拟 IP" with Projects / Assets; legacy positioning
             * URLs redirect into the project workflow. */}
            <Route path="virtual-ip/*" element={<VirtualIpApp />} />
            <Route path="virtual-ip-positioning/*" element={<VirtualIpLegacyPositioningRedirect />} />
            {/* VISTOERA_TENANT_OVERVIEW_HOME_V1 §5 — minimal read-only Storage
             * Detail route (not a left-nav item); see StorageDetailPage.tsx
             * docstring for why this one net-new route was justified. Never
             * feature-gated — storage usage/quota is orthogonal to Feature
             * Entitlement (spec §24: "空间 quota 与 feature entitlement
             * 分离"). */}
            <Route path="storage" element={<StorageDetailPage />} />
            <Route
              path="website"
              element={
                <RequireFeature featureKey="feature.website">
                  <WebsiteHomePage />
                </RequireFeature>
              }
            />
            <Route
              path="website/:siteId/*"
              element={
                <RequireFeature featureKey="feature.website">
                  <WebsiteSitePage />
                </RequireFeature>
              }
            />
            {/* Image Asset Center (library) vs Image Processing Workbench.
             * Declare the more-specific `images/process` route first. */}
            <Route
              path="images/process"
              element={
                <RequireFeature featureKey="feature.image_processing">
                  <WorkspaceImagesPage />
                </RequireFeature>
              }
            />
            {/* VISTOERA_FLAT_IMAGE_OPTIMIZATION_V1 §十一 — the editor route
             * must be declared before the bare list route below it. */}
            <Route
              path="images/flat-optimization/:chainId"
              element={
                <RequireFeature featureKey="feature.flat_image_optimization">
                  <FlatImageEditorPage />
                </RequireFeature>
              }
            />
            <Route
              path="images/flat-optimization"
              element={
                <RequireFeature featureKey="feature.flat_image_optimization">
                  <FlatImageListPage />
                </RequireFeature>
              }
            />
            <Route path="images" element={<ImageAssetCenterPage />} />
            {/* VISTOERA_VIDEO_DIRECTOR_UI_PREVIEW_VISIBILITY_FORENSIC_P0 —
             * `/video-generation/create` was a temporary secondary URL for
             * the Director workflow while it was being rolled out behind
             * an entry button. The Director workspace is now the PRIMARY
             * page for the bare `video-generation` route below, so this
             * old deep link just redirects there (kept only so any
             * previously-shared/bookmarked links keep working). */}
            <Route path="video-generation/create" element={<Navigate to="../video-generation" replace />} />
            {/* VISTOERA_VIDEO_CONTINUATION_V1_P0 §1 — the more-specific
             * `video-generation/continuation` route must be declared before
             * the bare `video-generation` route below it (same "declare the
             * specific route first" convention as `images/flat-optimization`
             * above). */}
            <Route
              path="video-generation/continuation"
              element={
                <RequireFeature featureKey="feature.video_continuation">
                  <VideoContinuationPage />
                </RequireFeature>
              }
            />
            {/* VISTOERA_VIDEO_ASSETS_V1 — the more-specific
             * `video-generation/assets` route must be declared before the
             * bare `video-generation` route below it (same "declare the
             * specific route first" convention as `.../continuation`
             * above). Deliberately ungated (no `RequireFeature`) — same
             * precedent as `IMAGES_SUB_ITEMS`'s 'assets' entry/route: a
             * library view over existing output rows, not itself a gated
             * generation capability. */}
            <Route path="video-generation/assets" element={<VideoAssetsPage />} />
            {/* VISTOERA_TENANT_AUDIO_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1
             * §6 — canonical route naming mirrors `images` (a single
             * library page IS the whole top-level section this round, no
             * sibling sub-pages yet) rather than `video-generation/assets`
             * (that section has multiple siblings — projects/creation/
             * continuation — `assets` disambiguates among them). Ungated
             * (no `RequireFeature`) — same precedent as the other asset
             * library routes above: a read-model view over already-
             * canonical sources, not itself a gated generation capability. */}
            <Route path="audio-assets" element={<AudioAssetsPage />} />
            {/* VISTOERA_TENANT_TEXT_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_
             * P0_V1 §7 — flat top-level route (mirrors `images`'s own
             * shape: the asset page IS the top-level route since 文本 has
             * exactly one child this round). Ungated, same precedent as
             * `images`/`video-generation/assets` — a library view over
             * already-canonical content, not itself a gated generation
             * capability. */}
            <Route path="text-assets" element={<TextAssetsPage />} />
            {/* VistoeraTASK-00525 - Team Management. `RequireTeamManager` is a display
             * gate only; every /team/* API call is authorised server-side. */}
            <Route
              path="team/*"
              element={
                <RequireTeamManager>
                  <TeamManagementPage />
                </RequireTeamManager>
              }
            />
            <Route
              path="video-generation/projects"
              element={
                <RequireFeature featureKey="feature.video_generation">
                  <VideoProjectListPage />
                </RequireFeature>
              }
            />
            {/* VISTOERA_VIDEO_DIRECTOR_UI_PREVIEW_VISIBILITY_FORENSIC_P0 —
             * the classic prompt-based create form + task history list
             * (formerly the bare `video-generation` page) moved here so it
             * stays fully reachable and functional. It must be declared
             * before the bare `video-generation` route below (same
             * "specific route first" convention). Nothing about the classic
             * page's own behavior changed — only its URL. */}
            <Route
              path="video-generation/classic"
              element={
                <RequireFeature featureKey="feature.video_generation">
                  <VideoGenerationPage />
                </RequireFeature>
              }
            />
            {/* VISTOERA_VIDEO_CREATION_DIRECTOR_WORKSPACE_UI_V1 /
             * VISTOERA_VIDEO_DIRECTOR_UI_PREVIEW_VISIBILITY_FORENSIC_P0 —
             * the AI Director-style creation workbench is now the PRIMARY
             * page rendered for the left-nav "视频生成" entry (this is what
             * users see immediately, no extra click required). The classic
             * form + task history remain one click away at
             * `video-generation/classic` (see above) and Step 5 of this
             * workflow still calls the same real LTX generation API, so no
             * generation capability was removed. Gated behind the SAME
             * feature key as before (this is a new UI on top of the same
             * capability, not a new entitlement). */}
            <Route
              path="video-generation"
              element={
                <RequireFeature featureKey="feature.video_generation">
                  <VideoDirectorCreatePage />
                </RequireFeature>
              }
            />
            {/* VISTOERA_PRODUCTION_CANVAS_V1_FOUNDATION_AND_INTERACTIVE_SHELL —
             * the spatial Canvas workspace for one project. Declared before
             * `content-production/:projectId` (same "specific route first"
             * convention as `images/flat-optimization/:chainId`) so
             * `/content-production/<id>/canvas` doesn't fall through to the
             * workbench page's own `:projectId` param matching "canvas" as
             * a literal segment. Ungated — same precedent as the workbench
             * route below it. */}
            <Route path="content-production/:projectId/canvas" element={<ProductionCanvasPage />} />
            {/* VISTOERA_CONTENT_PROJECT_DIRECT_CANVAS_AND_FULLSCREEN_CANVAS_SHELL_V1
             * §4/§42 — the old "AI 创作工作台" (`ContentProductionWorkbenchPage`,
             * still present on disk in case some other surface deep-links
             * into it, but no longer reachable from normal navigation) is
             * retired as a project landing page: this bare
             * `content-production/:projectId` URL — the OLD workbench
             * route AND the shape of any historical bookmark/internal link
             * a tenant may have saved — now redirects straight to the
             * project's canonical Production Canvas instead of rendering
             * the retired UI, so nothing 404s and there is exactly ONE
             * entry point into a project going forward. */}
            <Route path="content-production/:projectId" element={<ContentProductionProjectRedirect />} />
            <Route path="content-production" element={<ContentProductionListPage />} />
            {/* VISTOERA_COMPETITIVE_CONTENT_ANALYSIS_V1 §2 — one persistent
             * two-pane page (list + tabbed detail, same shape as
             * `AIChatPage`), never a separate list/detail route pair —
             * spec §21 explicitly forbids a right-side drawer taking over
             * the whole page. `:id` is optional/deep-link-only (which
             * `CompetitiveContentAnalysisPage` reads via `useParams`), so
             * the more-specific route is still declared first per this
             * file's own "specific route before bare route" convention.
             * Ungated (no `RequireFeature`) — same precedent as
             * `content-publishing`. */}
            <Route path="competitive-content-analysis/:id" element={<CompetitiveContentAnalysisPage />} />
            <Route path="competitive-content-analysis" element={<CompetitiveContentAnalysisPage />} />
            {/* VISTOERA_CONTENT_PUBLISHING_CENTER_UI_AND_DOMAIN_V1 §6 — the
             * more-specific `content-publishing/:id` detail route must be
             * declared before the bare list route below it (same
             * "declare the specific route first" convention as
             * `images/flat-optimization/:chainId`). Ungated (no
             * `RequireFeature`) — see `WorkspaceLayout.tsx`'s own nav
             * entry comment for why. */}
            <Route path="content-publishing/:id" element={<ContentPublishingDetailPage />} />
            <Route path="content-publishing" element={<ContentPublishingListPage />} />
            {/* VISTOERA_LOCAL_PUBLISHER_ENROLLMENT_GENERATION_PATH_AND_ADMIN_UI_P0
             * (reframed by VistoeraTASK-00073 into a single-installation
             * "本地发布助手" product surface — see LocalPublisherAgentPage.tsx
             * + localPublisherAgentState.ts) — tenant-facing
             * (workspace-scoped) UI for the Local Publisher companion
             * agent, deliberately a sibling of `content-publishing` rather
             * than living in Platform Admin: `LocalPublisherAgent`/
             * `PublisherAgentEnrollmentToken` are workspace-bound rows,
             * not global/cross-tenant like `WorkerEnrollmentToken`, so
             * this must render inside the per-workspace shell to respect
             * that boundary — the Local Publisher Agent is NOT a Platform
             * Worker and never enters PC1/PC3/PC4/PC5 compute scheduling.
             * Ungated (no `RequireFeature`) — same precedent as
             * `content-publishing` itself. */}
            <Route path="local-publishers" element={<LocalPublisherAgentPage />} />
            <Route
              path="domains"
              element={
                <RequireFeature featureKey="feature.custom_domain">
                  <WorkspaceDomainsPage />
                </RequireFeature>
              }
            />
            <Route
              path="publish"
              element={
                <RequireFeature featureKey="feature.site_publish">
                  <WorkspacePublishPage />
                </RequireFeature>
              }
            />
            <Route path="*" element={<Navigate to="overview" replace />} />
          </Route>
        </Routes>
      </WorkspaceThemeProvider>
    </RequireAuth>
  )
}
